import { useCallback, useEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { BsCheckCircle } from 'react-icons/bs';

import { creditsKeys } from '#creditFeature/models/credits/keys';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import { useAuth } from '#globalContexts/AuthProvider';
import FullscreenLayout from '#layouts/FullscreenLayout';
import { forceSyncCapabilities } from '#lib/authToken';
import { fetchUserSubscription } from '#settingsFeature/api/billingService';

// Stripe redirects the customer here the moment the payment succeeds, which is usually before its
// webhook has reached us. Until that arrives the account still looks unpaid, so the page waits for
// the subscription to appear instead of showing a plan the customer no longer has.
const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 10;

export const SubscriptionSuccessPage = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { refetchSubscription } = useAuth();
	const [isActivated, setIsActivated] = useState(false);
	const [tookTooLong, setTookTooLong] = useState(false);
	const cancelled = useRef(false);

	const finish = useCallback(async () => {
		await forceSyncCapabilities().catch(() => {});
		refetchSubscription();
		queryClient.invalidateQueries({ queryKey: creditsKeys.all });
	}, [refetchSubscription, queryClient]);

	useEffect(() => {
		cancelled.current = false;

		const waitForSubscription = async () => {
			for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
				if (cancelled.current) return;

				const { data } = await fetchUserSubscription().catch(() => ({ data: null }));
				if (cancelled.current) return;

				if (data) {
					await finish();
					if (!cancelled.current) setIsActivated(true);
					return;
				}

				await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
			}

			// The payment went through either way — Stripe told the customer so. Only our own view of
			// it is behind, so the page says that rather than implying the purchase failed.
			if (!cancelled.current) {
				await finish();
				setTookTooLong(true);
			}
		};

		void waitForSubscription();

		return () => {
			cancelled.current = true;
		};
	}, [finish]);

	const handleContinue = () => {
		navigate('/dashboard');
	};

	const isWaiting = !isActivated && !tookTooLong;

	return (
		<FullscreenLayout backdrop="success">
			<Card className="p-8 text-center">
				<div className="mb-8">
					<div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-success-200">
						{isWaiting ? (
							<div className="h-8 w-8 animate-spin rounded-full border-4 border-success-500 border-t-transparent" />
						) : (
							<BsCheckCircle className="h-7 w-7 text-success-700" />
						)}
					</div>

					<h1 className="mb-2 text-lg font-bold text-text">Zahlung erfolgreich!</h1>

					<p className="text-sm text-bgBase-800">
						{isWaiting
							? 'Wir richten Ihren Zugang gerade ein. Das dauert nur einen Moment.'
							: tookTooLong
								? 'Ihre Zahlung ist eingegangen. Die Freischaltung dauert noch einen Augenblick – laden Sie das Dashboard in Kürze neu.'
								: 'Vielen Dank für Ihr Abonnement. Ihr Account wurde erfolgreich aktiviert und Sie haben nun Zugang zu allen Features.'}
					</p>
				</div>

				<Button fullWidth variant="solid" size="md" onClick={handleContinue} disabled={isWaiting}>
					Zum Dashboard
				</Button>
			</Card>
		</FullscreenLayout>
	);
};
